import React, { useEffect, useState } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";

import { WarningBox } from "@components/cards/messageBox";
import {
  QuoteBuilderService,
  IVehicleQuoteSummary,
} from "@services/QuoteBuilder/QuoteBuilderService";

type Props = {
  country: string;
  vehicleSpecifier: string;
};

/**
 * Sold-vehicle indicator (TP-6846). For a vehicle-specific (POFO) plan, shows a non-blocking
 * advisory of how many OTHER customers have quoted / been onboarded on this physical vehicle
 * (keyed on vehicle_specifier). Advisory only — it must never block quoting, so on any error
 * it renders nothing (fail-silent). Renders nothing when the vehicle has no history, or when
 * the plan is not vehicle-specific ('0').
 */
export default function VehicleQuoteUsageIndicator({ country, vehicleSpecifier }: Props) {
  const msalContext = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [summary, setSummary] = useState<IVehicleQuoteSummary | null>(null);

  useEffect(() => {
    // '0' (or unset) = not vehicle-specific; nothing to show.
    if (!vehicleSpecifier || vehicleSpecifier === "0") {
      setSummary(null);
      return;
    }

    // Local fetch (NOT useServiceHook): the shared hook throws a full-screen error modal +
    // redirect on failure, which must never happen for an advisory. Fail silent instead.
    let cancelled = false;
    QuoteBuilderService.getVehicleQuoteSummary(country, vehicleSpecifier, {
      msalContext,
      isAuthenticated,
    })
      .then((resp) => {
        if (!cancelled) setSummary(resp);
      })
      .catch(() => {
        if (!cancelled) setSummary(null);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country, vehicleSpecifier]);

  if (!summary) return null;

  const { quoted_customer_count, onboarded_customer_count } = summary;
  if (quoted_customer_count === 0 && onboarded_customer_count === 0) return null;

  const lead =
    onboarded_customer_count > 0
      ? "This vehicle may already be sold."
      : "This vehicle has recent quoting activity.";
  const customerWord = quoted_customer_count === 1 ? "customer" : "customers";

  return (
    <div style={{ margin: "2.5vh 0vw" }} data-testid="vehicleQuoteUsageIndicator">
      <WarningBox
        testId="vehicleQuoteUsageWarning"
        message={
          <span>
            <strong>{lead}</strong> Quoted to {quoted_customer_count} {customerWord};{" "}
            {onboarded_customer_count} onboarded. You can still send this quote.
          </span>
        }
      />
    </div>
  );
}
